import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/Nav";

/** Iter-42: self-serve "Forgot password" step 1.
 *  Anyone (even not-logged-in) can POST their email here. Backend
 *  returns a generic 200 whether or not the email is registered, so we
 *  can't leak account existence. Currently the reset link is visible
 *  only to admins via /admin/free-users (admin-copy mode). Auto-email
 *  delivery via Resend is a follow-up.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Request received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white border border-[color:var(--border)] p-10">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-2">§ Reset your password</p>
          <h2 className="font-editorial text-3xl mb-4">Forgot password?</h2>
          {!sent ? (
            <>
              <p className="text-sm text-[color:var(--text-muted)] mb-6">
                Enter the email you signed up with. If we find a matching
                account, an admin will share a secure reset link with you
                within the next few minutes (auto-email is coming soon).
              </p>
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest mb-1">Email</span>
                  <input
                    type="email" required autoFocus value={email}
                    onChange={(e)=>setEmail(e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid="forgot-email"
                  />
                </label>
                <button type="submit" disabled={busy || !email.trim()}
                        className="btn-primary w-full"
                        data-testid="forgot-submit">
                  {busy ? "Sending…" : "Send reset request"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-confirmation">
              <p className="text-sm">
                <strong>Request received.</strong> If <span className="font-mono">{email}</span> matches an account, an admin will share a reset link with you shortly. You can close this window.
              </p>
              <p className="text-xs text-[color:var(--text-muted)] mt-4">
                Didn't get a link within an hour? Reach out to your BookForge admin.
              </p>
            </div>
          )}
          <p className="text-sm text-[color:var(--text-muted)] mt-6">
            Remembered it? <Link to="/login" className="link-underline text-[color:var(--text)]"
                                  data-testid="forgot-back-to-login">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
